<template>
    <main class="register-page">
        <div class="register-container">
            <div class="register-card">
                <!-- Header -->
                <div class="register-header">
                    <div class="logo-section">
                        <div class="logo-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                            </svg>
                        </div>
                        <h1>Create Account</h1>
                        <p>Join our learning community today</p>
                    </div>
                </div>

                <!-- Form -->
                <form @submit.prevent="handleSubmit" class="register-form">
                    <!-- Full Name -->
                    <div class="form-group" :class="{ 'has-error': errors.full_name, 'has-success': touched.full_name && !errors.full_name && form.full_name }">
                        <label for="full_name">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                <circle cx="12" cy="7" r="4"></circle>
                            </svg>
                            Full Name - (الاسم الكامل)
                        </label>
                        <input 
                            type="text" 
                            id="full_name" 
                            v-model="form.full_name" 
                            @blur="validateField('full_name')"
                            placeholder="Enter your full name"
                            :disabled="loading"
                        />
                        <span class="error-message" v-if="errors.full_name">{{ errors.full_name }}</span>
                        <span class="success-icon" v-if="touched.full_name && !errors.full_name && form.full_name">✓</span>
                    </div>

                    <!-- Email -->
                    <div class="form-group" :class="{ 'has-error': errors.email, 'has-success': touched.email && !errors.email && form.email }">
                        <label for="email">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                <polyline points="22,6 12,13 2,6"></polyline>
                            </svg>
                            Email Address - (البريد الإلكتروني)
                        </label>
                        <input 
                            type="email" 
                            id="email" 
                            v-model="form.email" 
                            @blur="validateField('email')"
                            placeholder="you@example.com"
                            :disabled="loading"
                        />
                        <span class="error-message" v-if="errors.email">{{ errors.email }}</span>
                        <span class="success-icon" v-if="touched.email && !errors.email && form.email">✓</span>
                    </div>

                    <!-- Phone -->
                    <div class="form-group" :class="{ 'has-error': errors.phone, 'has-success': touched.phone && !errors.phone && form.phone }">
                        <label for="phone">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                            </svg>
                            Phone Number - (رقم الهاتف)
                        </label>
                        <input 
                            type="tel" 
                            id="phone" 
                            v-model="form.phone" 
                            @blur="validateField('phone')"
                            placeholder="+1 (555) 123-4567"
                            :disabled="loading"
                        />
                        <span class="error-message" v-if="errors.phone">{{ errors.phone }}</span>
                        <span class="success-icon" v-if="touched.phone && !errors.phone && form.phone">✓</span>
                    </div>

                    <!-- Password -->
                    <div class="form-group" :class="{ 'has-error': errors.password, 'has-success': touched.password && !errors.password && form.password }">
                        <label for="password">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                            </svg>
                            Password - (كلمة المرور)
                        </label>
                        <div class="password-input-wrapper">
                            <input 
                                :type="showPassword ? 'text' : 'password'" 
                                id="password" 
                                v-model="form.password" 
                                @blur="validateField('password')"
                                @input="checkPasswordStrength"
                                placeholder="Create a strong password"
                                :disabled="loading"
                            />
                            <button type="button" class="toggle-password" @click="showPassword = !showPassword" tabindex="-1">
                                <svg v-if="!showPassword" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                    <circle cx="12" cy="12" r="3"></circle>
                                </svg>
                                <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                    <line x1="1" y1="1" x2="23" y2="23"></line>
                                </svg>
                            </button>
                        </div>
                        <!-- Password Strength Indicator -->
                        <div class="password-strength" v-if="form.password">
                            <div class="strength-bar">
                                <div :class="['strength-fill', passwordStrengthClass]" :style="{ width: passwordStrength + '%' }"></div>
                            </div>
                            <span :class="['strength-text', passwordStrengthClass]">{{ passwordStrengthText }}</span>
                        </div>
                        <span class="error-message" v-if="errors.password">{{ errors.password }}</span>
                        <span class="success-icon" v-if="touched.password && !errors.password && form.password">✓</span>
                    </div>

                    <!-- Confirm Password -->
                    <div class="form-group" :class="{ 'has-error': errors.confirm_password, 'has-success': touched.confirm_password && !errors.confirm_password && form.confirm_password }">
                        <label for="confirm_password">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                            </svg>
                            Confirm Password - (تأكيد كلمة المرور)
                        </label>
                        <div class="password-input-wrapper">
                            <input 
                                :type="showConfirmPassword ? 'text' : 'password'" 
                                id="confirm_password" 
                                v-model="form.confirm_password" 
                                @blur="validateField('confirm_password')"
                                placeholder="Confirm your password"
                                :disabled="loading"
                            />
                            <button type="button" class="toggle-password" @click="showConfirmPassword = !showConfirmPassword" tabindex="-1">
                                <svg v-if="!showConfirmPassword" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                    <circle cx="12" cy="12" r="3"></circle>
                                </svg>
                                <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                    <line x1="1" y1="1" x2="23" y2="23"></line>
                                </svg>
                            </button>
                        </div>
                        <span class="error-message" v-if="errors.confirm_password">{{ errors.confirm_password }}</span>
                        <span class="success-icon" v-if="touched.confirm_password && !errors.confirm_password && form.confirm_password">✓</span>
                    </div>
                    <!-- Address -->
                    <div class="form-group" :class="{ 'has-error': errors.address_line1, 'has-success': touched.address_line1 && !errors.address_line1 && form.address_line1 }">
                        <label for="address_line1">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                                <polyline points="9 22 9 12 15 12 15 22"></polyline>
                            </svg>
                            Street Address - (العنوان)
                        </label>
                        <input 
                            type="text" 
                            id="address_line1" 
                            v-model="form.address_line1" 
                            @blur="validateField('address_line1')"
                            placeholder="123 Main St"
                            :disabled="loading"
                        />
                        <span class="error-message" v-if="errors.address_line1">{{ errors.address_line1 }}</span>
                        <span class="success-icon" v-if="touched.address_line1 && !errors.address_line1 && form.address_line1">✓</span>
                    </div>

                    <div class="form-row">
                        <div class="form-group" :class="{ 'has-error': errors.city, 'has-success': touched.city && !errors.city && form.city }">
                            <label for="city">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <circle cx="12" cy="10" r="3"/><path d="M12 2a8 8 0 0 0-8 8c0 5.25 8 14 8 14s8-8.75 8-14a8 8 0 0 0-8-8z"/>
                                </svg>
                                City - (المدينة)
                            </label>
                            <input 
                                type="text" 
                                id="city" 
                                v-model="form.city" 
                                @blur="validateField('city')"
                                placeholder="Cairo"
                                :disabled="loading"
                            />
                            <span class="error-message" v-if="errors.city">{{ errors.city }}</span>
                            <span class="success-icon" v-if="touched.city && !errors.city && form.city">✓</span>
                        </div>

                        <div class="form-group" :class="{ 'has-error': errors.country, 'has-success': touched.country && !errors.country && form.country }">
                            <label for="country">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                                </svg>
                                Country - (الدولة)
                            </label>
                            <input 
                                type="text" 
                                id="country" 
                                v-model="form.country" 
                                @blur="validateField('country')"
                                placeholder="Egypt"
                                :disabled="loading"
                            />
                            <span class="error-message" v-if="errors.country">{{ errors.country }}</span>
                            <span class="success-icon" v-if="touched.country && !errors.country && form.country">✓</span>
                        </div>
                    </div>
                    <!-- Submit Button -->
                    <button type="submit" class="submit-btn" :disabled="loading || !isFormValid">
                        <span v-if="loading" class="spinner"></span>
                        <span v-else>Create Account</span>
                    </button>
                </form>

                <!-- Footer -->
                <div class="register-footer">
                    <p>Already have an account? <router-link :to="{ name: 'auth.login' }">Sign in</router-link></p>
                </div>
            </div>

            <!-- Success Modal -->
            <div v-if="showSuccessModal" class="modal-overlay" @click.self="closeModal">
                <div class="modal-content success-modal">
                    <div class="modal-icon">🎉</div>
                    <h2>Welcome Aboard!</h2>
                    <p>Your account has been created successfully. Please check your email to verify your account.</p>
                    <button @click="goToLogin" class="modal-btn">Go to Login</button>
                </div>
            </div>

            <!-- Error Modal -->
            <div v-if="showErrorModal" class="modal-overlay" @click.self="closeModal">
                <div class="modal-content error-modal">
                    <div class="modal-icon">⚠️</div>
                    <h2>Registration Failed</h2>
                    <p>{{ errorMessage }}</p>
                    <button @click="closeModal" class="modal-btn error">Try Again</button>
                </div>
            </div>
        </div>
    </main>
</template>

<script setup>
import { ref, reactive, computed } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();

const form = reactive({
    full_name: '',
    email: '',
    phone: '',
    address_line1: '',
    city: '',
    country: '',
    password: '',
    confirm_password: '',
});

const touched = reactive({
    full_name: false,
    email: false,
    phone: false,
    address_line1: false,
    city: false,
    country: false,
    password: false,
    confirm_password: false,
});

const errors = reactive({
    full_name: '',
    email: '',
    phone: '',
    address_line1: '',
    city: '',
    country: '',
    password: '',
    confirm_password: '',
});

const loading = ref(false);
const showPassword = ref(false);
const showConfirmPassword = ref(false);
const showSuccessModal = ref(false);
const showErrorModal = ref(false);
const errorMessage = ref('');
const passwordStrength = ref(0);
const passwordStrengthClass = ref('');
const passwordStrengthText = ref('');

const validators = {
    full_name: (value) => {
        if (!value.trim()) return 'Full name is required';
        if (value.trim().length < 2) return 'Name must be at least 2 characters';
        if (!/^[a-zA-Z\s\-']+$/.test(value)) return 'Name can only contain letters, spaces, hyphens and apostrophes';
        return '';
    },
    email: (value) => {
        if (!value.trim()) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address';
        return '';
    },
    phone: (value) => {
        if (!value.trim()) return 'Phone number is required';
        if (!/^[\d\s\-\+\(\)]+$/.test(value)) return 'Please enter a valid phone number';
        if (value.replace(/\D/g, '').length < 10) return 'Phone number must be at least 10 digits';
        return '';
    },
    address_line1: (value) => {
        if (!value.trim()) return 'Street address is required';
        if (value.trim().length < 5) return 'Please enter a valid street address';
        return '';
    },
    city: (value) => {
        if (!value.trim()) return 'City is required';
        if (value.trim().length < 2) return 'Please enter a valid city name';
        return '';
    },
    country: (value) => {
        if (!value.trim()) return 'Country is required';
        if (value.trim().length < 2) return 'Please enter a valid country name';
        return '';
    },
    password: (value) => {
        if (!value) return 'Password is required';
        if (value.length < 8) return 'Password must be at least 8 characters';
        if (!/(?=.*[a-z])/.test(value)) return 'Password must contain at least one lowercase letter';
        if (!/(?=.*[A-Z])/.test(value)) return 'Password must contain at least one uppercase letter';
        if (!/(?=.*\d)/.test(value)) return 'Password must contain at least one number';
        if (!/(?=.*[@$!%*?&])/.test(value)) return 'Password must contain at least one special character (@$!%*?&)';
        return '';
    },
    confirm_password: (value) => {
        if (!value) return 'Please confirm your password';
        if (value !== form.password) return 'Passwords do not match';
        return '';
    }
};

const isFormValid = computed(() => {
    return Object.keys(form).every(key => form[key].trim() !== '' && !errors[key]);
});

const validateField = (field) => {
    touched[field] = true;
    errors[field] = validators[field](form[field]);
    if (field === 'password' && form.confirm_password) {
        errors.confirm_password = validators.confirm_password(form.confirm_password);
    }
};

const checkPasswordStrength = () => {
    const pwd = form.password;
    let strength = 0;
    if (pwd.length >= 8) strength += 20;
    if (pwd.length >= 12) strength += 10;
    if (/(?=.*[a-z])/.test(pwd)) strength += 15;
    if (/(?=.*[A-Z])/.test(pwd)) strength += 15;
    if (/(?=.*\d)/.test(pwd)) strength += 20;
    if (/(?=.*[@$!%*?&])/.test(pwd)) strength += 20;
    passwordStrength.value = strength;
    if (strength < 40) { passwordStrengthClass.value = 'weak'; passwordStrengthText.value = 'Weak'; }
    else if (strength < 70) { passwordStrengthClass.value = 'medium'; passwordStrengthText.value = 'Medium'; }
    else { passwordStrengthClass.value = 'strong'; passwordStrengthText.value = 'Strong'; }
};

const validateAll = () => {
    let isValid = true;
    Object.keys(form).forEach(key => {
        validateField(key);
        if (errors[key]) isValid = false;
    });
    return isValid;
};

const handleSubmit = async () => {
    if (!validateAll()) return;
    loading.value = true;
    try {
        const response = await frappe.call({
            method: 'lms_website.api.auth.register',
            args: {
                full_name: form.full_name.trim(),
                email: form.email.trim().toLowerCase(),
                phone: form.phone.trim(),
                address_line1: form.address_line1.trim(),
                city: form.city.trim(),
                country: form.country.trim(),
                password: form.password
            }
        });
        if (response.message?.success) {
            showSuccessModal.value = true;
        } else {
            throw new Error(response.message?.message || 'Registration failed');
        }
    } catch (error) {
        errorMessage.value = error.message || 'An error occurred during registration. Please try again.';
        showErrorModal.value = true;
    } finally {
        loading.value = false;
    }
};

const closeModal = () => {
    showSuccessModal.value = false;
    showErrorModal.value = false;
};

const goToLogin = () => {
    router.push({ name: 'auth.login' });
};
</script>

<style scoped>
.register-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(180deg, #fff 0%, #7d7d7d 100%);
    padding: 20px;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.register-container {
    width: 100%;
    max-width: 480px;
    animation: slideUp 0.6s ease-out;
}
.form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}

@media (max-width: 480px) {
    .form-row {
        grid-template-columns: 1fr;
    }
}
@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.register-card {
    background: white;
    border-radius: 24px;
    padding: 40px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.register-header {
    text-align: center;
    margin-bottom: 32px;
}

.logo-section {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.logo-icon {
    width: 64px;
    height: 64px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
}

.logo-icon svg {
    width: 32px;
    height: 32px;
    color: white;
}

.register-header h1 {
    font-size: 28px;
    font-weight: 700;
    color: #1a202c;
    margin: 0 0 8px 0;
}

.register-header p {
    color: #718096;
    font-size: 16px;
    margin: 0;
}

.register-form {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.form-group {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.form-group label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #4a5568;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.form-group label svg {
    width: 16px;
    height: 16px;
    color: #667eea;
}

.form-group input[type="text"],
.form-group input[type="email"],
.form-group input[type="tel"],
.form-group input[type="password"] {
    padding: 14px 16px;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    font-size: 16px;
    transition: all 0.3s ease;
    background: #f7fafc;
    color: #2d3748;
}

.form-group input:focus {
    outline: none;
    border-color: #667eea;
    background: white;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-group.has-error input {
    border-color: #fc8181;
    background: #fff5f5;
}

.form-group.has-success input {
    border-color: #48bb78;
    background: #f0fff4;
}

.password-input-wrapper {
    position: relative;
}

.password-input-wrapper input {
    width: 100%;
    padding-right: 50px;
}

.toggle-password {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    color: #a0aec0;
    transition: color 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
}

.toggle-password:hover {
    color: #667eea;
}

.toggle-password svg {
    width: 20px;
    height: 20px;
}

.error-message {
    font-size: 13px;
    color: #e53e3e;
    display: flex;
    align-items: center;
    gap: 4px;
    animation: shake 0.5s ease-in-out;
}

@keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-5px); }
    75% { transform: translateX(5px); }
}

.success-icon {
    position: absolute;
    right: 16px;
    top: 38px;
    color: #48bb78;
    font-weight: bold;
    font-size: 18px;
}

.password-strength {
    margin-top: 8px;
}

.strength-bar {
    height: 4px;
    background: #e2e8f0;
    border-radius: 2px;
    overflow: hidden;
    margin-bottom: 4px;
}

.strength-fill {
    height: 100%;
    border-radius: 2px;
    transition: all 0.3s ease;
}

.strength-fill.weak {
    background: #fc8181;
}

.strength-fill.medium {
    background: #f6ad55;
}

.strength-fill.strong {
    background: #48bb78;
}

.strength-text {
    font-size: 12px;
    font-weight: 600;
}

.strength-text.weak {
    color: #e53e3e;
}

.strength-text.medium {
    color: #dd6b20;
}

.strength-text.strong {
    color: #38a169;
}

/* Checkbox Styling */
.checkbox-group {
    margin-top: 8px;
}

.checkbox-label {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    cursor: pointer;
    font-size: 14px;
    color: #4a5568;
    text-transform: none;
    letter-spacing: 0;
    font-weight: 400;
    line-height: 1.5;
}

.checkbox-label input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
}

.checkmark {
    width: 20px;
    height: 20px;
    min-width: 20px;
    border: 2px solid #cbd5e0;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    margin-top: 2px;
}

.checkbox-label input:checked + .checkmark {
    background: #667eea;
    border-color: #667eea;
}

.checkbox-label input:checked + .checkmark::after {
    content: '✓';
    color: white;
    font-size: 12px;
    font-weight: bold;
}

.checkbox-text a {
    color: #667eea;
    text-decoration: none;
    font-weight: 600;
}

.checkbox-text a:hover {
    text-decoration: underline;
}

/* Submit Button */
.submit-btn {
    margin-top: 8px;
    padding: 16px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
}

.submit-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
}

.submit-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.spinner {
    display: inline-block;
    width: 20px;
    height: 20px;
    border: 3px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: white;
    animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

/* Divider */
.divider {
    display: flex;
    align-items: center;
    text-align: center;
    margin: 16px 0;
    color: #a0aec0;
    font-size: 14px;
}

.divider::before,
.divider::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #e2e8f0;
}

.divider span {
    padding: 0 16px;
}

/* Social Button */
.social-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 14px;
    background: white;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    color: #4a5568;
    cursor: pointer;
    transition: all 0.3s ease;
}

.social-btn:hover:not(:disabled) {
    background: #f7fafc;
    border-color: #cbd5e0;
    transform: translateY(-1px);
}

.social-btn svg {
    width: 20px;
    height: 20px;
}

/* Footer */
.register-footer {
    text-align: center;
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid #e2e8f0;
}

.register-footer p {
    color: #718096;
    font-size: 15px;
    margin: 0;
}

.register-footer a {
    color: #667eea;
    text-decoration: none;
    font-weight: 600;
}

.register-footer a:hover {
    text-decoration: underline;
}

/* Modals */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

.modal-content {
    background: white;
    border-radius: 24px;
    padding: 40px;
    text-align: center;
    max-width: 400px;
    width: 90%;
    animation: scaleIn 0.3s ease;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

@keyframes scaleIn {
    from {
        transform: scale(0.9);
        opacity: 0;
    }
    to {
        transform: scale(1);
        opacity: 1;
    }
}

.modal-icon {
    font-size: 64px;
    margin-bottom: 16px;
}

.modal-content h2 {
    font-size: 24px;
    font-weight: 700;
    color: #1a202c;
    margin: 0 0 12px 0;
}

.modal-content p {
    color: #718096;
    font-size: 16px;
    line-height: 1.6;
    margin: 0 0 24px 0;
}

.modal-btn {
    padding: 14px 32px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
}

.modal-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
}

.modal-btn.error {
    background: linear-gradient(135deg, #fc8181 0%, #e53e3e 100%);
}

/* Responsive */
@media (max-width: 640px) {
    .register-card {
        padding: 24px;
        border-radius: 20px;
    }

    .register-header h1 {
        font-size: 24px;
    }

    .logo-icon {
        width: 56px;
        height: 56px;
    }
}
</style>